import type {
  LeisureSearchRequestBody,
  LocalRegionsQueryParams,
  PlaceGeo2addressParams,
  LocalSearchRequestBody,
  RecommendationLocalAccommodationRequest,
  RecommendationLocalLeisureRequest,
  UniversalSearchRequestBody,
  UniversalSearchHeaderRequestBody,
} from '@weberus/shared-types';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { isNumber } from 'lodash-es';
import {
  fetchCategory,
  fetchLeisureSearch,
  fetchLeisureSearchCount,
  fetchLeisureSearchHeader,
} from '@/app/(pages)/leisure/api';
import {
  fetchGeo2Address,
  fetchLocalAccommodationMap,
  fetchLocalRegions,
  fetchLocalSearch,
  fetchLocalSearchCount,
  fetchLocalSearchHeader,
  fetchRecommendationLocalAccommodation,
  fetchRecommendationLocalLeisure,
} from '@/app/(pages)/local/api';
import {
  fetchGlobalListUniversalSearch,
  fetchUniversalSearch,
  fetchUniversalSearchCount,
  fetchUniversalSearchHeader,
  fetchUniversalSearchMap,
} from '@/app/(pages)/results/api';
import { QueryKeys } from './query-keys';

export const listQueryKeys = createQueryKeys(QueryKeys.list, {
  // ==================== 레저 ==================== //
  /** 레저 카테고리 정보 조회 */
  getLeisureCategory: () => ({
    queryKey: [''],
    queryFn: () => fetchCategory(),
  }),
  /** 레저 검색 리스트 조회 */
  getLeisureSearch: (args: LeisureSearchRequestBody) => ({
    queryKey: [args],
    queryFn: ({ pageParam = 1 }) => fetchLeisureSearch({ ...args, page: pageParam }),
  }),
  /** 레저 검색 헤더 정보 조회 */
  getLeisureSearchHeader: (args: LeisureSearchRequestBody, headers?: Headers) => ({
    queryKey: [args],
    queryFn: () => fetchLeisureSearchHeader(args, headers),
  }),
  /** 레저 검색 개수 조회 */
  getLeisureSearchCount: (args: LeisureSearchRequestBody) => ({
    queryKey: [args],
    queryFn: () => fetchLeisureSearchCount(args),
  }),

  // ==================== 국내숙소 ==================== //
  /** 국내숙소 지역 상권(region) 리스트 조회 */
  getLocalRegions: (params: LocalRegionsQueryParams) => ({
    queryKey: [''],
    queryFn: () => fetchLocalRegions(params),
  }),
  /* 위치기반 주소 조회 */
  getGeo2Address: (args: PlaceGeo2addressParams) => ({
    queryKey: [args],
    queryFn: () => fetchGeo2Address(args),
  }),
  /* [V3] 국내숙소 PLP/SRP 헤더 영역 조회 */
  getLocalSearchHeader: (args: LocalSearchRequestBody, headers?: Headers) => ({
    queryKey: [args],
    queryFn: () => fetchLocalSearchHeader(args, headers),
  }),
  /* [V3] 국내숙소 PLP/SRP 리스트 조회 */
  getLocalSearch: (
    args: LocalSearchRequestBody,
    hashedMemberNo: string | undefined,
    pageKey?: string | undefined,
    headers?: Headers,
  ) => ({
    queryKey: [args, hashedMemberNo, pageKey],
    queryFn: ({ pageParam = 1 }) => fetchLocalSearch({ ...args, page: pageParam }, headers),
  }),
  /** [V3] 국내숙소 지도 조회 */
  getLocalAccommodationMap: (args: LocalSearchRequestBody, hashedMemberNo: string | undefined) => ({
    queryKey: [args, hashedMemberNo],
    queryFn: ({ pageParam = 1 }) => fetchLocalAccommodationMap({ ...args, page: pageParam }),
  }),
  /* [V3] 국내숙소 PLP/SRP CTA 버튼 개수 조회 */
  getLocalSearchCount: (args: LocalSearchRequestBody) => ({
    queryKey: [args],
    queryFn: () => fetchLocalSearchCount(args),
  }),
  /* 국내숙소 PLP/SRP 추천 숙소 상품 조회 */
  getRecommendationLocalAccommodation: (args: RecommendationLocalAccommodationRequest) => ({
    queryKey: [args],
    queryFn: () => fetchRecommendationLocalAccommodation(args),
  }),
  /* 국내숙소 PLP/SRP 추천 레저 상품 조회 */
  getRecommendationLocalLeisure: (args: RecommendationLocalLeisureRequest) => ({
    queryKey: [args],
    queryFn: () => fetchRecommendationLocalLeisure(args),
  }),

  // ==================== 해외숙소 ==================== //
  getGlobalSearch: (args: UniversalSearchRequestBody, headers?: Headers) => ({
    queryKey: [args],
    queryFn: () => fetchGlobalListUniversalSearch(args, headers),
  }),

  // ==================== 통합SRP ==================== //

  /* [V1] 통합 SRP 헤더 영역 조회 */
  getUniversalSearchHeader: (args: UniversalSearchHeaderRequestBody, headers?: Headers) => ({
    queryKey: [args],
    queryFn: () => fetchUniversalSearchHeader(args, headers),
  }),

  /* [V1] 통합 SRP 리스트 조회 */
  getUniversalSearch: (
    args: UniversalSearchRequestBody,
    hashedMemberNo: string | undefined,
    pageKey?: string | undefined,
    category?: string | undefined,
    headers?: Headers,
  ) => ({
    queryKey: [args, hashedMemberNo, pageKey, category],
    queryFn: ({ pageParam }) =>
      fetchUniversalSearch(
        { ...args, ...(pageParam && isNumber(pageParam) ? { page: pageParam } : { cursor: pageParam }) },
        headers,
      ),
  }),

  /* [V1] 통합 SRP CTA 버튼 개수 개수 조회 */
  getUniversalSearchCount: (args: Omit<UniversalSearchRequestBody, 'sort' | 'page'>) => ({
    queryKey: [args],
    queryFn: () => fetchUniversalSearchCount(args),
  }),

  /* [V1] 통합 SRP 지도 조회 */
  getUniversalSearchMap: (args: UniversalSearchRequestBody, hashedMemberNo: string | undefined) => ({
    queryKey: [args, hashedMemberNo],
    queryFn: ({ pageParam = 1 }) => fetchUniversalSearchMap({ ...args, page: pageParam }),
  }),
});
